import React from 'react';
import { ChevronDown, User, Download } from 'lucide-react';
import { Order, OrderItem, OrderTimeline, OrderStatus, VoucherInfo } from '../types';
import { exportOrderToExcel } from '../utils/excelExport';
import { formatDateLong, formatDateShort } from '../utils/dateFormat';

interface OrderDetailsProps {
  order: Order;
  orderItems: OrderItem[];
  vouchers?: VoucherInfo[];
  onBack: () => void;
}

export default function OrderDetails({ order, orderItems, vouchers, onBack }: OrderDetailsProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handleExportOrder = () => {
    exportOrderToExcel(order, orderItems);
  };

  const getTimeline = (): OrderTimeline[] => {
    const timeline: OrderTimeline[] = [
      {
        step: 'pedido_realizado',
        label: 'Pedido realizado',
        date: order.purchasedAt,
        completed: true
      },
      {
        step: 'pagamento_aprovado',
        label: 'Pagamento aprovado',
        date: order.paymentApprovedAt,
        completed: !!order.paymentApprovedAt
      },
      {
        step: 'faturamento_realizado',
        label: 'Faturamento realizado',
        date: order.invoicedAt,
        completed: !!order.invoicedAt
      },
      {
        step: 'entrega',
        label: 'Entrega',
        date: order.deliveredAt,
        completed: !!order.deliveredAt
      }
    ];
    if (order.canceledAt) {
      timeline.push({
        step: 'cancelado',
        label: 'Cancelado',
        date: order.canceledAt,
        completed: !!order.canceledAt
      });
    }
    return timeline;
  };

  const timeline = getTimeline();
  const currentStatusIndex = timeline.findIndex(t => t.step === order.status);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">
      <div className="mx-auto">
        <button
          onClick={onBack}
          className="mb-6 text-slate-600 hover:text-slate-900 font-medium transition-colors flex items-center gap-2"
        >
          <ChevronDown className="rotate-90" size={20} />
          Voltar para lista de pedidos
        </button>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="flex items-center gap-3 bg-[rgb(29,78,216)] text-white px-6 py-3">
            <User size={24} />
            <div>
              <h1 className="text-xl font-bold text-white">{order.storeName}</h1>
              <p className="text-sm text-blue-100">CNPJ: {order.clientId}</p>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              <div>
                <h3 className="text-sm font-semibold text-slate-500 mb-1">
                  Vendedor
                </h3>
                <p className="text-lg font-semibold text-slate-900">
                  {order.sellerId || 'Não assistida'}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-500 mb-1">
                  Número do pedido
                </h3>
                <p className="text-xl font-bold text-slate-900">
                  {order.orderNumber}
                </p>
                <p className="text-sm text-slate-600 mt-2">
                  Valor total
                </p>
                <p className="text-lg font-bold text-slate-900">
                  {formatCurrency(order.totalValue)}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-500 mb-1">
                  Forma de pagamento
                </h3>
                <p className="text-lg font-semibold text-slate-900">
                  {order.paymentMethod}
                </p>
                <p className="text-sm text-slate-600 mt-2">
                  Comprado em
                </p>
                <p className="text-sm font-medium text-slate-900">
                  {formatDateLong(order.purchasedAt)}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-500 mb-1">
                  Status do pedido
                </h3>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-[rgb(37,99,235)] rounded-full"></div>
                  <span className="text-lg font-semibold text-slate-900">
                    {timeline[currentStatusIndex]?.label}
                  </span>
                </div>
                {timeline[currentStatusIndex]?.date && (
                  <p className="text-sm text-slate-600">
                    {formatDateShort(timeline[currentStatusIndex]?.date)}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 p-6 space-y-8">
            <div>
              

              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                <h2 className="text-xl font-bold text-slate-900">Detalhes do pedido</h2>
                <button
                  onClick={handleExportOrder}
                  className="flex items-center gap-2 px-5 py-2.5 text-[rgb(37,99,235)] border-2 border-[rgb(37,99,235)] rounded-xl hover:bg-blue-50 transition-colors font-medium"
                >
                  <Download size={18} />
                  Exportar pedido
                </button>
              </div>

              <div className="bg-slate-50 rounded-xl p-6 mb-6 relative">
                {order.distribuidor ? 
                <h3 className="text-base font-bold text-slate-900 mb-1">
                  {order.distribuidor}
                </h3> : null }
                <p className="text-sm font-semibold text-slate-700 mb-4">
                  Status do pedido
                </p>

                <div className="relative">
                  <div className="flex justify-between items-start mb-2">
                    {timeline.map((item, index) => (
                      <div
                        key={item.step}
                        className="flex flex-col items-center flex-1 relative"
                      >
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                            item.completed
                              ? 'bg-[rgb(37,99,235)]'
                              : 'bg-slate-300 border-2 border-slate-200'
                          }`}
                        >
                          {item.completed && (
                            <div className="w-3 h-3 bg-white rounded-full"></div>
                          )}
                        </div>

                        {index < timeline.length - 1 && (
                          <div
                            className={`absolute top-4 left-1/2 w-full h-0.5 ${
                              timeline[index + 1]?.completed
                                ? 'bg-[rgb(37,99,235)]'
                                : 'bg-slate-300'
                            }`}
                            style={{
                              left: '50%',
                              right: '-50%',
                              width: 'calc(100% + 16px)',
                              marginLeft: '16px'
                            }}
                          ></div>
                        )}

                        <p className="text-xs font-medium text-slate-700 mt-3 text-center max-w-[100px]">
                          {item.label}
                        </p>
                        {item.date && (
                          <p className="text-xs text-slate-500 mt-1 text-center">
                            {formatDateShort(item.date)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {order.status === 'cancelado' && (
                  <div className="absolute inset-0 bg-red-600/90 rounded-xl flex flex-col items-center justify-center z-20 backdrop-blur-sm">
                    <div className="text-center px-6">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-10 h-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-2">Pedido Cancelado</h3>
                      <p className="text-white/90 text-sm">Este pedido foi cancelado e não será processado</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-slate-700">
                        SKU
                      </th>
                      <th className="text-left py-4 px-4 text-xs font-semibold text-slate-700">
                        Produto
                      </th>
                      <th className="text-center py-4 px-4 text-xs font-semibold text-slate-700">
                        Quantidade
                      </th>
                      <th className="text-center py-4 px-4 text-xs font-semibold text-slate-700">
                        Quantidade faturada
                      </th>
                      <th className="text-right py-4 px-4 text-xs font-semibold text-slate-700">
                        Valor unitário
                      </th>
                      <th className="text-right py-4 px-4 text-xs font-semibold text-slate-700">
                        Valor total
                      </th>
                      <th className="text-right py-4 px-4 text-xs font-semibold text-slate-700">
                        Valor total faturado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {orderItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                        <td className="py-4 px-4 text-sm text-slate-900">
                          {item.sku}
                        </td>
                        <td className="py-4 px-4 text-sm text-slate-900">
                          {item.productName}
                        </td>
                        <td className="py-4 px-4 text-sm text-slate-900 text-center">
                          {item.quantity}
                        </td>
                        <td className="py-4 px-4 text-sm text-slate-900 text-center">
                          {item.invoicedQuantity}
                        </td>
                        <td className="py-4 px-4 text-sm text-slate-900 text-right">
                          {formatCurrency(item.unitPrice)}
                        </td>
                        <td className="py-4 px-4 text-sm text-slate-900 text-right">
                          {formatCurrency(item.totalPrice)}
                        </td>
                        <td className="py-4 px-4 text-sm text-slate-900 text-right">
                          {formatCurrency(item.invoicedTotal)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
